import React, { useMemo } from "react";
import { SelectInput, TextInput } from "../../../controls";
import type { SelectOption } from "../../../controls/SelectInput/SelectInput";
import {
  filterFns,
  type Column,
  type FilterFn,
} from "@tanstack/react-table";
import { DateTime } from "luxon";

export const CATEGORY_ALL = "All";

// Column meta keys this filter reads. Not part of the default react-table
// ColumnMeta, so we narrow locally at the access sites.
interface TableFilterColumnMeta {
  filterType?: string;
  filterOptions?: SelectOption["value"][];
  filterAdditionalOptions?: SelectOption["value"][];
  autoFocus?: boolean;
}

const getFilterMeta = (
  column: Column<unknown>
): TableFilterColumnMeta | undefined =>
  column.columnDef?.meta as TableFilterColumnMeta | undefined;

// FilterFn is invariant in its row type, so these row-type-agnostic helpers
// are typed FilterFn<any> — matching @tanstack/react-table's own filterFns —
// so consumers can assign them to columns of any concrete row type.
export const multiSelectFilterFn: FilterFn<any> = (
  row,
  columnId,
  filterValue
) => {
  const rowValue = row.getValue(columnId);

  if (Array.isArray(rowValue)) {
    return Boolean(
      rowValue.filter((value) => filterValue.includes(value)).length ||
        filterValue.includes("")
    );
  }
  return (
    filterValue.includes(row.getValue(columnId)) || filterValue.includes("")
  );
};

export const selectFilterFn: FilterFn<any> = (
  row,
  columnId,
  filterValue
) => {
  return filterValue === "" || filterValue === row.getValue(columnId);
};

export const dateFilterFn: FilterFn<any> = (row, columnId, filterValue) => {
  const rowValue = row.getValue<string>(columnId);

  if (filterValue === CATEGORY_ALL) {
    return true;
  }

  const rowDateTime = DateTime.fromISO(rowValue);
  const limit = DateTime.utc().minus({ days: filterValue }).startOf("day");
  return rowDateTime > limit;
};

interface TableFilterTypeConfig {
  filterFn: FilterFn<any>;
  filterType: string;
}

export const tableFilterTypes: Record<
  "MULTI_SELECT" | "TEXT" | "DATE" | "SELECT",
  TableFilterTypeConfig
> = {
  MULTI_SELECT: {
    filterFn: multiSelectFilterFn,
    filterType: "multi-select",
  },
  TEXT: {
    filterFn: filterFns.includesString,
    filterType: "text",
  },
  DATE: {
    filterFn: dateFilterFn,
    filterType: "date-select",
  },
  SELECT: {
    filterFn: selectFilterFn,
    filterType: "select",
  },
};

interface TableFilterProps {
  column: Column<unknown>;
}

const TableFilter: React.FC<TableFilterProps> = ({ column }) => {
  const DateFilterOptions: SelectOption[] = [
    { label: CATEGORY_ALL, value: CATEGORY_ALL },
    { label: "Today", value: 0 },
    { label: "Last Week", value: 7 },
    { label: "Month", value: 30 },
    { label: "Quarter", value: 90 },
  ];

  const columnFilterValue = column.getFilterValue() as
    | string
    | string[]
    | undefined;

  const sortedUniqueValues = useMemo<SelectOption[]>(() => {
    let arrayFromTableData = Array.from(column.getFacetedUniqueValues().keys());
    const meta = getFilterMeta(column);
    const usersAdditionalOptions = meta?.filterAdditionalOptions || [];
    usersAdditionalOptions?.forEach((option) => {
      if (!arrayFromTableData.includes(option)) {
        arrayFromTableData.push(option);
      }
    });
    const usersOptions = meta?.filterOptions;
    if (usersOptions) {
      arrayFromTableData = usersOptions;
    }
    return [{ label: "All", value: "" }].concat(
      arrayFromTableData
        .filter(
          (value) => value !== "" && value !== null && value !== undefined
        )
        .sort()
        .map((value) => ({ value, label: value }))
    );
    // Dependency array contains function execution according to the explanation in https://betterviewcloud.atlassian.net/browse/WAK-9194
  }, [column.getFacetedUniqueValues()]);

  const handleMultiSelectChange = (value: SelectOption[]) => {
    if (!value.length) {
      column.setFilterValue([""]);
    } else {
      const newFilterHasAllValue = value?.find((option) => option.value === "");
      const currentFilterHasAllValue = Array.isArray(columnFilterValue)
        ? columnFilterValue.includes("")
        : false;

      if (currentFilterHasAllValue) {
        value = value?.filter((option) => option.value !== "");
      } else if (newFilterHasAllValue) {
        value = value?.filter((option) => option.value === "");
      }

      column.setFilterValue(value.map((val) => val.value)); // Set undefined to remove the filter entirely
    }
  };

  const handleTextChange = (e: { target: { value: string } }) => {
    column.setFilterValue(e.target.value);
  };

  switch (getFilterMeta(column)?.filterType) {
    case tableFilterTypes.MULTI_SELECT.filterType: {
      return (
        <SelectInput
          name={"table-filter-dropdown"}
          data-testid={"table-filter-dropdown"}
          menuWrapClassName={"table-header-filter"}
          isMulti
          value={sortedUniqueValues.filter((option) => {
            return Array.isArray(columnFilterValue)
              ? columnFilterValue.includes(option.value)
              : false;
          })}
          onChange={handleMultiSelectChange}
          options={sortedUniqueValues}
          setDefaultSingleOption={false}
        />
      );
    }
    case tableFilterTypes.SELECT.filterType: {
      return (
        <SelectInput
          name={"table-filter-dropdown"}
          data-testid={"table-filter-select-dropdown"}
          menuWrapClassName={"table-header-filter"}
          value={sortedUniqueValues.find(
            (option) => option.value === columnFilterValue
          )}
          options={sortedUniqueValues}
          setDefaultSingleOption={false}
          onChange={(e: SelectOption) => column.setFilterValue(e.value)}
        />
      );
    }
    case tableFilterTypes.DATE.filterType: {
      return (
        <SelectInput
          name={"table-filter-dropdown"}
          data-testid={"table-filter-date-dropdown"}
          menuWrapClassName={"table-header-filter"}
          value={DateFilterOptions.filter(
            (option) => option.value === columnFilterValue
          )}
          options={DateFilterOptions}
          setDefaultSingleOption={false}
          onChange={(e: SelectOption) => column.setFilterValue(e.value)}
        />
      );
    }
    case tableFilterTypes.TEXT.filterType: {
      return (
        <TextInput
          leftIcon={"seem-icon-search"}
          data-testid={"table-filter-text"}
          type={"text"}
          value={(columnFilterValue ?? "") as string}
          placeholder={"Type to Search..."}
          isClearable
          onChange={handleTextChange}
          noMargin
          applyAutoFocus={getFilterMeta(column)?.autoFocus}
        />
      );
    }
    default: {
      return null;
    }
  }
};

export default TableFilter;
